import React, { useRef, useEffect } from "react";
import { Modal, Avatar, Skeleton, Image } from "antd";
import {
  UserOutlined,
  RobotOutlined,
  TeamOutlined, // Para "agent"
} from "@ant-design/icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import moment from "moment";

interface Message {
  message: string;
  timestamp: string; // ISO string
  role: "user" | "assistant" | "agent";
  message_type?: string; // "text" | "image" | "audio" | "document"
}

interface AgentTakeover {
  agent_takeover_dates: string;
  agent_takeover_count: number;
}

interface ChatDetails {
  chat_uuid: string;
  establishment_id: string;
  chat_platform_type: string;
  message_count?: number;
  history: Message[];
  agent_takeover?: AgentTakeover[];
  name_user: string;
  is_controlled: boolean;
  ttl?: number;
}

interface ChatModalProps {
  visible: boolean;
  onClose: () => void;
  chatDetails: ChatDetails | null;
  loadingHistory?: boolean; // Por si quieres mostrar un spin de carga del historial
}

/** Formatear fecha/hora usando Moment.js */
const formatTimestamp = (timestamp: string) => {
  return moment(timestamp).format("DD/MM/YYYY HH:mm:ss");
};

/** Traducir plataforma a algo más legible */
const getPlatformName = (platformType: string) => {
  switch (platformType.toLowerCase()) {
    case "internal":
      return "Interna";
    case "whatsapp":
      return "WhatsApp";
    default:
      return platformType;
  }
};

/**
 * Renderiza el contenido de un mensaje según su tipo:
 * - image => <Image ...>
 * - audio => <audio ...>
 * - document => <a ...>
 * - text => Markdown
 */
const formatMessageContent = (msg: Message) => {
  const { message, message_type } = msg;

  // Manejo de imágenes
  if (message_type === "image") {
    return (
      <Image
        src={message}
        alt="Imagen enviada"
        style={{ maxWidth: "100%", borderRadius: "8px", marginTop: "10px" }}
      />
    );
  }

  // Manejo de audios
  if (message_type === "audio") {
    return (
      <audio controls style={{ marginTop: 10, maxWidth: "100%" }}>
        <source src={message} type="audio/mpeg" />
        Tu navegador no soporta reproducir audio.
      </audio>
    );
  }

  // Manejo de documentos
  if (message_type === "document") {
    return (
      <a
        href={message}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#1890ff", wordWrap: "break-word" }}
      >
        Abrir documento
      </a>
    );
  }

  // Cualquier otro caso (o sin message_type) => se asume texto/markdown
  return (
    <ReactMarkdown
      children={message}
      remarkPlugins={[remarkGfm]}
      // Opcionalmente, puedes personalizar render de listas, imágenes dentro de markdown, etc.
      components={{
        img: ({ node, ...props }) => (
          <Image
            src={props.src || ""}
            alt={props.alt || "Imagen enviada"}
            width={200}
          />
        ),
      }}
    />
  );
};

const ChatModal: React.FC<ChatModalProps> = ({
  visible,
  onClose,
  chatDetails,
  loadingHistory,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  /** Hace scroll al final del contenedor cuando se abre el modal o llegan nuevos mensajes */
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (visible) {
      // Usar un pequeño delay para esperar a que renderice primero
      setTimeout(scrollToBottom, 100);
    }
  }, [chatDetails?.history, visible]);

  return (
    <Modal
      open={visible}
      title={
        chatDetails
          ? `Detalles del Chat ${chatDetails.chat_uuid} (Plataforma: ${getPlatformName(
              chatDetails.chat_platform_type
            )})`
          : "Cargando..."
      }
      onCancel={onClose}
      footer={null}
      width={800}
      style={{ padding: 0 }}
      destroyOnClose
    >
      {loadingHistory ? (
        /** Si estás cargando el historial, puedes mostrar un Skeleton o un Spin */
        <Skeleton active />
      ) : !chatDetails ? (
        <Skeleton active />
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "60vh",
            overflow: "hidden",
            backgroundColor: "#f5f5f5",
            borderRadius: "10px",
            padding: 0,
          }}
        >
          {/* Contenedor de mensajes */}
          <div
            style={{
              flex: 1,
              padding: "20px",
              overflowY: "auto",
              backgroundColor: "#ffffff",
              borderRadius: "10px",
            }}
          >
            {chatDetails.history.map((msg, index) => {
              // Decide si el mensaje se muestra a la izquierda o derecha
              // "assistant" y "agent" a la derecha, "user" a la izquierda
              const isAssistant = msg.role === "assistant";
              const isAgent = msg.role === "agent";
              const isUser = msg.role === "user";
              const bubbleAlign = isAssistant || isAgent ? "flex-end" : "flex-start";

              // Opcional: Diferenciar fondos para user/agent
              let bubbleColor = "#e6f7ff"; // User por defecto
              if (isAgent) {
                bubbleColor = "#dbf6d7"; // Fondo distinto si es "agent"
              }
              if (isAssistant) {
                bubbleColor = "#f0f0f0";
              }

              // Avatar según rol
              let avatarIcon = <UserOutlined />;
              let avatarColor = "#52c41a"; // verde para user
              if (isAssistant) {
                avatarIcon = <RobotOutlined />;
                avatarColor = "#1890ff";
              } else if (isAgent) {
                avatarIcon = <TeamOutlined />;
                avatarColor = "#44bd32";
              }

              // Determinar maxWidth basado en el tipo de mensaje
              let messageMaxWidth = "80%"; // Por defecto para texto
              if (msg.message_type === "image") {
                messageMaxWidth = "30%";
              }

              return (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    marginBottom: "20px",
                    justifyContent: bubbleAlign,
                  }}
                >
                  {/* Para user/agent, ponemos el avatar al principio; para assistant/agent, al final */}
                  {!(isAssistant || isAgent) && (
                    <Avatar
                      icon={avatarIcon}
                      style={{ backgroundColor: avatarColor, marginRight: "10px" }}
                    />
                  )}

                  {/* Contenedor del texto con maxWidth condicional */}
                  <div style={{ maxWidth: messageMaxWidth }}>
                    <div
                      style={{
                        backgroundColor: bubbleColor,
                        padding: "10px 15px",
                        borderRadius: "15px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                        textAlign: "left",
                      }}
                    >
                      {formatMessageContent(msg)}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#888",
                        marginTop: "5px",
                        textAlign: isAssistant || isAgent ? "right" : "left",
                      }}
                    >
                      {formatTimestamp(msg.timestamp)}
                    </div>
                  </div>

                  {(isAssistant || isAgent) && (
                    <Avatar
                      icon={avatarIcon}
                      style={{ backgroundColor: avatarColor, marginLeft: "10px" }}
                    />
                  )}
                </div>
              );
            })}
            {/* Para forzar el scroll hasta el final */}
            <div ref={messagesEndRef} />
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ChatModal;